// Distribution SCHEDULES (distribution design section 12 — Figma redesign).
// A schedule is a recurring config: a start month, an optional end month, and
// exactly two monthly day-ranges that the backend auto-runs.

export type DistributionScheduleStatus = 'UPCOMING' | 'RUNNING' | 'COMPLETED';

/** Sentinel day value meaning "Last day of the month" (matches backend). */
export const LAST_DAY = 99;

/** Lowest selectable literal day. */
export const MIN_DAY = 1;

/** Highest selectable literal day (capped < 28 so it exists in every month). */
export const MAX_LITERAL_DAY = 27;

/** Number of day-ranges a schedule must have (per Figma). */
export const REQUIRED_RANGES = 2;

export interface DistributionScheduleRange {
  sortOrder: number;
  startDay: number;
  endDay: number;
  label: string;
}

export interface DistributionSchedule {
  id: number;
  startPeriod: string;      // yyyy-MM-01
  endPeriod?: string | null;
  status: DistributionScheduleStatus;
  rangesDisplay: string;    // "1st - 19th | 20th - Last day"
  ranges: DistributionScheduleRange[];
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface DistributionScheduleRangeInput {
  startDay: number;
  endDay: number;
}

/** Figma default day-ranges for a new schedule: 1st–19th and 20th–Last day. */
export const DEFAULT_RANGES: DistributionScheduleRangeInput[] = [
  { startDay: 1, endDay: 19 },
  { startDay: 20, endDay: LAST_DAY },
];

export interface DistributionScheduleRequest {
  startPeriod: string;          // yyyy-MM-01
  endPeriod?: string | null;
  ranges: DistributionScheduleRangeInput[];
}

export interface DayOption {
  value: number;
  label: string;
}

/** Day dropdown options: 1st…27th + "Last day" (per Figma + locked decision). */
export function dayOptions(): DayOption[] {
  const opts: DayOption[] = [];
  for (let d = MIN_DAY; d <= MAX_LITERAL_DAY; d++) {
    opts.push({ value: d, label: ordinal(d) });
  }
  opts.push({ value: LAST_DAY, label: 'Last day' });
  return opts;
}

export function ordinal(day: number): string {
  if (day === LAST_DAY) {
    return 'Last day';
  }
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${day}th`;
  }
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
}
